/**
 * Payment Data Models
 *
 * Core data structures for payment operations across websites and gateways.
 */

// ThirdPartySystemId: Integer IDs for third-party systems (must match @pillar-portal/database/enums.ts)
// WARNING: Never change existing IDs, only add new ones

import { z } from 'zod';
import type { TransactionDetails } from './payment.types';
import type { LosslessNumber } from './lossless-json-codec';
import { JsonLosslessNumberSchema } from './schemas/common.schemas';
import { GatewayLifecycleRequestSchema } from './schemas/ipg/gateway-lifecycle-request.schemas';
import type { GatewayLifecycleRequest } from './types/ipg/gateway-lifecycle-request.types';

export type { TransactionDetails };

export const CNP_INDETERMINATE_ERROR =
    'The card payment result is indeterminate. Do not retry this payment; reconcile it first.';

export const CreditCardDetailsSchema = z.object({
    cardNumber: z.string(),
    expiryMonth: z.string(),
    expiryYear: z.string(),
    cvv: z.string(),
    cardholderName: z.string(),
});
export type CreditCardDetails = z.infer<typeof CreditCardDetailsSchema>;

// AchDetails
export const AchDetailsSchema = z.object({
    routingNumber: z.string(),
    accountNumber: z.string(),
    accountType: z.enum(['checking', 'savings']),
    accountHolderName: z.string(),
});
export type AchDetails = z.infer<typeof AchDetailsSchema>;

// StoredCreditCardDetails
export const StoredCreditCardDetailsSchema = z.object({
    tokenType: z.string(),
    cardToken: z.string(),
});
export type StoredCreditCardDetails = z.infer<typeof StoredCreditCardDetailsSchema>;

// StoredAchDetails
export const StoredAchDetailsSchema = z.object({
    storedAccountId: z.string(),
});
export type StoredAchDetails = z.infer<typeof StoredAchDetailsSchema>;

// PaymentResult
export interface PaymentResult {
    gatewaySystemName: string;
    gatewayTransactionId?: string;
    paymentTransactionId?: number;
    paymentTransactionUuid?: string;
    success: boolean;
    processedAmount: LosslessNumber;
    message?: string;
    gatewayResponse?: unknown;
    gatewayRequest?: GatewayLifecycleRequest;
}

export const PaymentResultSchema: z.ZodType<PaymentResult> = z.object({
    gatewaySystemName: z.string(),
    gatewayTransactionId: z.string().optional(),
    paymentTransactionId: z.number().int().positive().optional(),
    paymentTransactionUuid: z.string().uuid().optional(),
    success: z.boolean(),
    processedAmount: JsonLosslessNumberSchema,
    message: z.string().optional(),
    gatewayResponse: z.unknown().optional(),
    gatewayRequest: GatewayLifecycleRequestSchema.optional(),
});

// TerminalAbortResult
export const TerminalAbortResultSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
    gatewayResponse: z.unknown().optional(),
});
export type TerminalAbortResult = z.infer<typeof TerminalAbortResultSchema>;

// TerminalResult
export const TerminalResultSchema = z.object({
    success: z.boolean(),
    terminalId: z.string().optional(),
    status: z.string().optional(),
    message: z.string().optional(),
});
export type TerminalResult = z.infer<typeof TerminalResultSchema>;

// TransactionStatus
export const TransactionStatusEnum = z.enum(['pending', 'completed', 'failed', 'cancelled']);
export type TransactionStatus = z.infer<typeof TransactionStatusEnum>;

// AbortResult
export const AbortResultSchema = z.object({
    success: z.boolean(),
    message: z.string().optional(),
});
export type AbortResult = z.infer<typeof AbortResultSchema>;

// PaymentMethodType
export const PaymentMethodTypeEnum = z.enum(['card', 'ach', 'terminal', 'saved']);
export type PaymentMethodType = z.infer<typeof PaymentMethodTypeEnum>;

// CardPaymentData
export const CardPaymentDataSchema = z.object({
    cardNumber: z.string(),
    expiryDate: z.string(),
    cvv: z.string(),
    cardholderName: z.string(),
});
export type CardPaymentData = z.infer<typeof CardPaymentDataSchema>;

// CardValidationResult
export const CardValidationResultSchema = z.object({
    isValid: z.boolean(),
    errors: z.array(z.string()),
    cardType: z.enum(['visa', 'mastercard', 'amex', 'discover', 'unknown']).optional(),
});
export type CardValidationResult = z.infer<typeof CardValidationResultSchema>;

// ACHPaymentData
export const ACHPaymentDataSchema = z.object({
    routingNumber: z.string(),
    accountNumber: z.string(),
    accountType: z.enum(['checking', 'savings']),
    accountHolderName: z.string(),
    bankName: z.string().optional(),
});
export type ACHPaymentData = z.infer<typeof ACHPaymentDataSchema>;

// ACHValidationResult
export const ACHValidationResultSchema = z.object({
    isValid: z.boolean(),
    errors: z.array(z.string()),
    bankName: z.string().optional(),
    bankVerified: z.boolean().optional(),
});
export type ACHValidationResult = z.infer<typeof ACHValidationResultSchema>;

// TerminalPaymentData
export const TerminalPaymentDataSchema = z.object({
    amount: z.string(),
    description: z.string(),
    terminalId: z.string().optional(),
});
export type TerminalPaymentData = z.infer<typeof TerminalPaymentDataSchema>;

// SavedPaymentMethod
export const SavedPaymentMethodSchema = z.object({
    id: z.string(),
    type: z.enum(['card', 'ach']),
    displayName: z.string(),
    lastFour: z.string(),
    expiryDate: z.string().optional(),
    bankName: z.string().optional(),
    accountType: z.enum(['checking', 'savings']).optional(),
    isDefault: z.boolean(),
    createdAt: z.number(),
    lastUsed: z.number().optional(),
});
export type SavedPaymentMethod = z.infer<typeof SavedPaymentMethodSchema>;

// Payment workflow states
export type PaymentWorkflowState =
    | 'initializing'
    | 'validating'
    | 'processing'
    | 'waiting_for_response'
    | 'completed'
    | 'failed'
    | 'cancelled';

// Payment workflow step
export interface PaymentWorkflowStep {
    id: string;
    name: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed';
    startTime?: number;
    endTime?: number;
    duration?: number;
    message?: string;
}

// Payment workflow progress
export interface PaymentWorkflowProgress {
    workflowId: string;
    currentStep: number;
    totalSteps: number;
    steps: PaymentWorkflowStep[];
    state: PaymentWorkflowState;
    startTime: number;
    estimatedCompletion?: number;
}

// Gateway configuration
export interface GatewayConfig {
    name: string;
    endpoint: string;
    apiKey: string;
    environment: 'sandbox' | 'production';
    timeout: number;
    retryAttempts: number;
}

// Payment gateway response
export interface GatewayResponse {
    success: boolean;
    gatewayTransactionId?: string;
    status: string;
    message?: string;
    errorCode?: string;
    rawResponse?: unknown;
    timestamp: number;
}

// Payment validation rules
export interface PaymentValidationRules {
    minAmount: number;
    maxAmount: number;
    allowedCurrencies: string[];
    requiredFields: string[];
    customValidation?: (data: any) => ValidationResult;
}

export interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings?: string[];
}

// Payment processing options
export interface PaymentProcessingOptions {
    autoCapture: boolean;
    savePaymentMethod: boolean;
    sendReceipt: boolean;
    customFields?: Record<string, any>;
    metadata?: Record<string, any>;
}

// Payment batch processing
export interface PaymentBatch {
    id: string;
    name: string;
    payments: PaymentBatchItem[];
    status: 'pending' | 'processing' | 'completed' | 'failed';
    createdAt: number;
    completedAt?: number;
}

export interface PaymentBatchItem {
    id: string;
    paymentData: CardPaymentData | ACHPaymentData | TerminalPaymentData;
    transactionDetails: TransactionDetails;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    result?: PaymentResult;
}

// Payment analytics
export interface PaymentAnalytics {
    totalTransactions: number;
    totalAmount: number;
    successRate: number;
    averageProcessingTime: number;
    paymentMethodBreakdown: Record<PaymentMethodType, number>;
    errorBreakdown: Record<string, number>;
    timeRange: {
        start: number;
        end: number;
    };
}
